import {mappedFields} from '@/lib/StandardFields';
import type {MappedField} from '@/lib/StandardFields';
import {getCompGroupDisplayFieldLabels, getDisplayFieldLabelsForDocument} from '@/services/SalesforceService';
import {MapService} from '@/services/MapService';
import {extractCompetitiveBuildingDisplayFields, getCompDisplayFields} from '../InfoWindowContent/helpers/competitive';
import {exportFormatFunctions} from '../InfoWindowContent/helpers/shared';
import {getCompPresentation} from '@/services/ContentPresentation';
import {
  MIN_OVERLAY_VISIBILITY_ZOOM,
  filterDisplayFields,
  includeCompNameFields,
} from './exportDisplayFieldRules';
import type {
  ExportComp,
  ExportDisplayField,
  ExportFieldSetting,
  ExportFieldSettings,
  FormattedMarkerContent,
} from './exportDisplayFieldRules';

/**
 * Discovers the fields available for export to a shared map, per collection.
 */

export type {
  ExportFieldSetting,
  ExportFieldSettings,
  ExportSettingsTab,
  FieldSelections,
} from './exportDisplayFieldRules';

/** Converts a raw marker into info-window content; may reject or yield null for broken markers. */
type FormatFn = (marker: unknown, id?: string) =>
  Promise<FormattedMarkerContent | null> | FormattedMarkerContent | null;

/** A comp category in the settings tree: always created with children. */
type CompCategory = ExportFieldSetting & {children: ExportFieldSetting[]};

/**
 * The Salesforce config that drives a standardFields group's export field ordering. `compGroup`
 * names one of the document's nested comp groups; without it the document's flat
 * standardFieldOrder is used.
 */
type SalesforceConfigSource = {document: string; compGroup?: string};

interface ExportMarker {
  markerData?: {id?: string};
  [key: string]: unknown;
}

interface ExportFeature {
  featureType: string;
  icon?: {name: string};
  displayFields?: ExportDisplayField[];
  comps?: ExportComp[];
  [key: string]: unknown;
}

interface ExportOverlay {
  visible?: boolean;
  isTaxOverlay?: boolean;
  features: {values(): Iterable<ExportFeature>};
  [key: string]: unknown;
}

const getOrCreate = <K, V>(map: Map<K, V>, key: K, create: () => V): V => {
  let value = map.get(key);
  if (value === undefined) {
    value = create();
    map.set(key, value);
  }
  return value;
};

/** The project collections all sync from Deal__c */
const PROJECT_GROUP_KEYS = [
  'industrialProject',
  'multiFamilyProject',
  'acquisitionProject',
  'dataCenterProject',
  'multiFamilyAcquisitionProject',
];

const projectConfigSources = (): Record<string, SalesforceConfigSource> => {
  const sources: Record<string, SalesforceConfigSource> = {};
  for (const groupKey of PROJECT_GROUP_KEYS) {
    sources[groupKey] = {document: 'Deal__c'};
    sources[`${groupKey}Utilities`] = {document: 'Deal__c', compGroup: 'Utilities'};
  }
  return sources;
};

const SF_CONFIG_BY_GROUP: Record<string, SalesforceConfigSource> = {
  'industrialBuilding': {document: 'Property__c'},
  'industrialBuildingLease': {document: 'Unit__c'},
  'industrialBuildingActive Lease Deal': {document: 'Lease__c'},
  'industrialBuildingCapital': {document: 'Investment__c'},
  'industrialBuildingCompliance Requirements': {document: 'Property__c', compGroup: 'Compliance Requirements'},
  ...projectConfigSources(),
};

/** Ordered field labels from a group's flat Salesforce config, or null to use the fallback. */
const configLabelsFor = async (groupKey: string): Promise<string[] | null> => {
  const source = SF_CONFIG_BY_GROUP[groupKey];
  if (!source || source.compGroup) return null;
  const labels = await getDisplayFieldLabelsForDocument(source.document);
  return labels.length ? labels : null;
};

/** Prefixes labels that repeat across comps of one type, matching how selections are stored. */
const compChildLabel = (label: string, compName: string): string =>
  includeCompNameFields.includes(label) ? `${compName} ${label}` : label;

/** Ordered seed labels for a comp category's children. */
const compSeedLabelsFor = async (compGroupKey: string, compName: string): Promise<string[]> => {
  const source = SF_CONFIG_BY_GROUP[compGroupKey];
  if (source?.compGroup) {
    const compLabels = await getCompGroupDisplayFieldLabels(source.document, source.compGroup);
    if (compLabels.length) {
      return compLabels.flatMap((comp) => comp.labels.map((label) => compChildLabel(label, comp.compName)));
    }
  }
  const fallbackLabels = (mappedFields?.[compGroupKey] || []).map((field) => field.label);
  const labels = (await configLabelsFor(compGroupKey)) ?? fallbackLabels;
  return labels.map((label) => compChildLabel(label, compName));
};

/**
 * Given an array of `markerData` for buildings, returns the Display Fields available for export.
 */
const getBuildingExportableFields = async (
  markers: ExportMarker[],
  collectionName: string,
  formatFn: FormatFn,
): Promise<ExportFieldSettings> => {
  const exportFieldSettings: ExportFieldSettings = new Map();

  // Remove trailing 's' from collectionName if present
  const standardFieldName = collectionName.endsWith('s') ? collectionName.slice(0, -1) : collectionName;

  // Seed the standard field ordering from the Salesforce config, falling back to mappedFields
  const fallbackLabels = (mappedFields?.[standardFieldName] || []).map((field) => field.label);
  const seedLabels = (await configLabelsFor(standardFieldName)) ?? fallbackLabels;

  // Add the standard fields to the export field settings with a value of false
  const collectionSettings: ExportFieldSetting[] =
    seedLabels.map((label) => ({label, value: false}));
  exportFieldSettings.set(collectionName, collectionSettings);

  // Get the info window content for easy access to display fields.
  // A marker that fails to format is skipped rather than blanking the whole dialog.
  const contents: FormattedMarkerContent[] = [];
  for (const marker of markers) {
    try {
      const content = await formatFn(marker, marker?.markerData?.id);
      if (content) {
        contents.push(content);
      }
    } catch (error) {
      console.error(`Failed to format marker '${marker?.markerData?.id}' for export:`, error);
    }
  }

  for (const content of contents) {
    if (!content.displayFields?.length) continue;

    filterDisplayFields(content.displayFields).forEach((field) => {
      if (field.label === 'Name') return;
      // Check if the field already exists in the array
      const existingSetting = collectionSettings.find((setting) => setting.label === field.label);
      if (existingSetting) {
        existingSetting.value = true;
      } else {
        collectionSettings.push({label: field.label, value: true});
      }
    });
  }

  // Add the comp display fields in a second pass so every top-level field lands ahead of comp categories
  for (const content of contents) {
    for (const comp of content.comps ?? []) {
      const compDisplayFields = filterDisplayFields(comp.displayFields)!;
      if (!compDisplayFields.length) continue;

      const compGroupKey = `${standardFieldName}${comp.type}`;
      const compNamePrefix = `${comp.name ?? compDisplayFields.find((f) => f.label === 'Name')?.value}`;
      const compDisplayName = getCompPresentation(comp.type as string).getPluralizedName();

      // Check if the comp already exists in the array.
      // Match comp categories only (have `children`); a top-level field could share the label.
      let compFieldSettings = collectionSettings.find(
        (setting): setting is CompCategory =>
          setting.label === compDisplayName && setting.children !== undefined,
      );
      if (!compFieldSettings) {
        const compSeedLabels = await compSeedLabelsFor(compGroupKey, compNamePrefix);
        compFieldSettings = {
          label: compDisplayName,
          value: true,
          collapsed: false,
          // Add the standard fields to the export field settings with a value of false
          children: compSeedLabels.map((label) => ({label, value: false})),
        };
        collectionSettings.push(compFieldSettings);
      }
      const compCategory = compFieldSettings;

      // Mark standard fields as checked if they exist in the filtered displayFields (have non-null values)
      // This ensures all standard fields appear in export with proper checked state
      compDisplayFields.forEach((field) => {
        if (field.label === 'Name') return;

        const label = compChildLabel(field.label, compNamePrefix);
        // Check if the field already exists in the array
        const existingCompSetting = compCategory.children.find((setting) => setting.label === label);
        if (existingCompSetting) {
          // Mark as checked only if field has a non-null value
          existingCompSetting.value = true;
        } else {
          // Allow data-driven fields to be added even if they're not in the standard list
          compCategory.children.push({label, value: true});
        }
      });
    }
  }

  // Sort comps by order
  collectionSettings.sort((a, b) => {
    const order = ['Lease Deal', 'Lease', 'Capital', 'Land Sale', 'Building Sale'];
    return order.indexOf(a.label) - order.indexOf(b.label);
  });

  // Filter out fields with no data across any exported marker
  exportFieldSettings.forEach((fields, key) => {
    exportFieldSettings.set(key, fields.filter((field) => {
      if (field.children) {
        // Remove comp children that no marker had data for
        field.children = field.children.filter((child) => child.value);
        return field.children.length > 0;
      }
      return field.value;
    }));
  });

  // Return an empty map if no fields are selected
  return (exportFieldSettings.get(collectionName) ?? []).length === 0 ? new Map() : exportFieldSettings;
};

/**
 * Given an array of comps, returns the Display Fields available for export. Includes both the parent and comp fields.
 */
const getCompExportableFields = (comps: ExportComp[]): ExportFieldSettings => {
  const exportFieldSettings: ExportFieldSettings = new Map();
  const parentStandardFields = mappedFields['competitiveBuilding'];

  // Parent entry first: settings-map key order drives tab order.
  const parentSettings: ExportFieldSetting[] =
    parentStandardFields.map((field) => ({label: field.label, value: false}));
  if (comps.length) {
    exportFieldSettings.set('competitiveBuildings', parentSettings);
  }

  comps.forEach((comp) => {
    const compType = comp.type as string;
    const compStandardFields = mappedFields[`competitiveBuilding${compType}`] || [];
    const compTypeSettings = getOrCreate(exportFieldSettings, compType, () =>
      compStandardFields.map((field) => ({label: field.label, value: false})));

    const compDisplayFields = filterDisplayFields(getCompDisplayFields(comp));
    // extractCompetitiveBuildingDisplayFields is destructive, so we need to make a copy of the parent comp
    const parentIWContent = extractCompetitiveBuildingDisplayFields({...(comp.parent as Record<string, unknown>)});
    const parentDisplayFields = filterDisplayFields(parentIWContent.displayFields);

    compDisplayFields.forEach((field) => {
      if (field.label === 'Name') return;
      const existingCompSetting = compTypeSettings.find((setting) => setting.label === field.label);
      if (existingCompSetting) {
        existingCompSetting.value = true;
      } else {
        compTypeSettings.push({label: field.label, value: true});
      }
    });

    parentDisplayFields.forEach((field) => {
      if (field.label === 'Name') return;
      const existingParentSetting = parentSettings.find((setting) => setting.label === field.label);
      if (existingParentSetting) {
        existingParentSetting.value = true;
      } else {
        parentSettings.push({label: field.label, value: true});
      }
    });
  });

  exportFieldSettings.forEach((fields, key) => {
    const filteredFields = fields.filter((field) => field.value);
    if (filteredFields.length) {
      exportFieldSettings.set(key, filteredFields);
    } else {
      exportFieldSettings.delete(key);
    }
  });

  return exportFieldSettings;
};

/**
 * Given an array of Features, returns a map of exportable fields per Feature.
 */
const getFeatureExportableFields = (features: ExportFeature[]): ExportFieldSettings => {
  const exportFieldSettings: ExportFieldSettings = new Map();

  // Pass 1: Collect all top-level and comp fields, keeping them separate for now
  // Also track all unique fields per comp type so we can ensure they're all represented
  const separatedByType = new Map<string, {topLevel: ExportFieldSetting[]; comps: CompCategory[]}>();
  const allCompFieldsByType = new Map<string, Set<string>>(); // Maps compType -> Set of all unique field labels

  // Process each feature to collect top-level fields and comps separately
  for (const feature of features) {
    if (!feature.displayFields || !feature.displayFields.length) continue;

    // Determine the lookup key for this feature type
    let lookup = feature.featureType;
    if (feature.featureType === 'placeMarker') {
      lookup = feature.icon!.name;
    } else if (feature.featureType.startsWith('blockPlan')) {
      lookup = 'blockPlan';
    }

    const separated = getOrCreate(separatedByType, lookup, () => ({topLevel: [], comps: []}));

    // Add top-level displayFields
    feature.displayFields.forEach((df) => {
      const existingSetting = separated.topLevel.find((setting) => setting.label === df.label);
      if (!existingSetting) {
        separated.topLevel.push({label: df.label, value: true});
      }
    });

    // Process comps as expandable categories
    if (feature.comps && feature.comps.length) {
      feature.comps.forEach((comp) => {
        if (!comp.displayFields || !comp.displayFields.length) return;

        const compDisplayName = getCompPresentation(comp.type as string).getPluralizedName();

        // Track all unique fields for this comp type
        const uniqueFieldsForType = getOrCreate(allCompFieldsByType, compDisplayName, () => new Set<string>());

        // Check if this comp category already exists
        let compFieldSettings = separated.comps.find((setting) => setting.label === compDisplayName);

        if (!compFieldSettings) {
          // Create a new expandable category for this comp type
          compFieldSettings = {
            label: compDisplayName,
            value: true,
            collapsed: false,
            children: [],
          };
          separated.comps.push(compFieldSettings);
        }
        const compCategory = compFieldSettings;

        // Filter comp displayFields using the same rules as elsewhere, and skip Name fields
        const filteredCompDisplayFields = filterDisplayFields(comp.displayFields);
        filteredCompDisplayFields.forEach((field) => {
          if (field.label === 'Name' || field.label === 'Building Name') return;

          // Apply the same label formatting that filterExcludedFields() expects
          // Use comp.name directly, like getBuildingExportableFields() does
          const label = includeCompNameFields.includes(field.label) ? `${comp.name ?? ''} ${field.label}`.trim() : field.label;

          // Track this field as part of the unique set for this comp type
          uniqueFieldsForType.add(label);

          const existingCompSetting = compCategory.children.find((setting) => setting.label === label);
          if (existingCompSetting) {
            existingCompSetting.value = true;
          } else {
            compCategory.children.push({label, value: true});
          }
        });
      });
    }
  }

  // Pass 1.5: Ensure all unique fields are represented in children, even if not in all comps
  // This allows users to deselect fields that appear in some comps but not others
  separatedByType.forEach((separated) => {
    separated.comps.forEach((compFieldSettings) => {
      const uniqueFieldsForType = allCompFieldsByType.get(compFieldSettings.label);
      if (uniqueFieldsForType) {
        uniqueFieldsForType.forEach((fieldLabel) => {
          // Check if this field is already in the children
          const existingSetting = compFieldSettings.children.find((setting) => setting.label === fieldLabel);
          if (!existingSetting) {
            // Add the field with value: false since it doesn't exist in all instances
            compFieldSettings.children.push({label: fieldLabel, value: false});
          }
        });
      }
    });
  });

  // Pass 2: Combine topLevel first, then comps, into final array structure
  separatedByType.forEach((separated, key) => {
    const combinedFields: ExportFieldSetting[] = [];

    // Add all top-level fields first
    const filteredTopLevel = separated.topLevel.filter((field) => field.value);
    combinedFields.push(...filteredTopLevel);

    // Add all comp categories second (keep all children for user to select/unselect)
    const filteredComps = separated.comps.filter((field) => {
      // Don't filter children here - keep all children so user can select/unselect them during export
      return field.value || field.children.length > 0;
    });
    combinedFields.push(...filteredComps);

    if (combinedFields.length) {
      exportFieldSettings.set(key, combinedFields);
    }
  });

  return exportFieldSettings;
};

/**
 * Given a collection of overlays and a feature type
 * Returns the available display fields for exporting that feature type to shareable maps
 */
const getOverlayFields = (overlayCollection: ExportOverlay[]): ExportFieldSettings => {
  const exportFieldSettings: ExportFieldSettings = new Map();

  // Pass 1: Separate top-level fields from comp categories by feature type
  const separatedByType = new Map<string, {topLevel: ExportFieldSetting[]; comps: CompCategory[]}>();

  overlayCollection.forEach((overlay) => {
    if (!overlay.visible || MapService.getMap()!.getZoom()! < MIN_OVERLAY_VISIBILITY_ZOOM || overlay.isTaxOverlay) {
      return;
    }

    for (const feature of overlay.features.values()) {
      if (!feature.displayFields || !feature.displayFields.length) continue;

      let lookup = feature.featureType;
      if (feature.featureType.startsWith('blockPlan')) {
        lookup = 'blockPlan';
      }

      const separated = getOrCreate(separatedByType, lookup, () => ({topLevel: [], comps: []}));

      // Add top-level displayFields
      feature.displayFields.forEach((df) => {
        const existingSetting = separated.topLevel.find((setting) => setting.label === df.label);
        if (!existingSetting) {
          separated.topLevel.push({label: df.label, value: true});
        }
      });

      // Process comps if they exist on the feature
      feature.comps?.forEach((comp) => {
        // Filter display fields to only include type-specific standard fields as defined in standardFields.js
        // Check both industrialBuilding and competitiveBuilding definitions
        let compDisplayFields = comp.displayFields;
        let mappedFieldsKey = `industrialBuilding${comp.type}`;
        if (!(mappedFieldsKey in mappedFields)) {
          mappedFieldsKey = `competitiveBuilding${comp.type}`;
        }

        // Only apply field filtering if the comp type is explicitly defined in standardFields
        if (mappedFieldsKey in mappedFields) {
          const compTypeStandardFields = mappedFields[mappedFieldsKey];
          const standardFieldLabels = new Set(compTypeStandardFields.map((f) => f.label));
          // Filter to only include fields defined for this comp type
          const hasDataFields = (field: ExportDisplayField) => field.displayFields && field.displayFields.length > 0;
          compDisplayFields = compDisplayFields!.filter(
            (field) => standardFieldLabels.has(field.label) || hasDataFields(field),
          );
        }

        compDisplayFields = filterDisplayFields(compDisplayFields);
        if (!compDisplayFields?.length) return;
        const dataFields = compDisplayFields;

        const compName = comp.name;
        const compDisplayName = getCompPresentation(comp.type as string).getPluralizedName();

        // Check if the comp type already exists in the comps array
        let compFieldSettings = separated.comps.find((setting) => setting.label === compDisplayName);
        if (!compFieldSettings) {
          // First, add all standard fields for this comp type with value: false
          let compTypeStandardFields: MappedField[] = [];
          let standardFieldsKey = `industrialBuilding${comp.type}`;
          if (!(standardFieldsKey in mappedFields)) {
            standardFieldsKey = `competitiveBuilding${comp.type}`;
          }
          if (standardFieldsKey in mappedFields) {
            compTypeStandardFields = mappedFields[standardFieldsKey];
          }

          compFieldSettings = {
            label: compDisplayName,
            value: true,
            collapsed: false,
            children: compTypeStandardFields.map((field) => ({label: field.label, value: false})),
          };
          separated.comps.push(compFieldSettings);
        }
        const compCategory = compFieldSettings;

        dataFields.forEach((field) => {
          if (field.label === 'Name') return;

          const label = includeCompNameFields.includes(field.label)
            ? `${compName ?? dataFields.find((f) => f.label === 'Name')?.value} ${field.label}`
            : field.label;

          // Check if the field already exists in the children array
          const existingCompSetting = compCategory.children.find(
            (setting) => setting.label === label,
          );
          if (!existingCompSetting) {
            compCategory.children.push({label, value: true});
          }
        });
      });
    }
  });

  // Pass 2: Combine topLevel first, then comps, into final array structure
  separatedByType.forEach((separated, key) => {
    const combinedFields: ExportFieldSetting[] = [];

    // Add all top-level fields first
    const filteredTopLevel = separated.topLevel.filter((field) => field.value);
    combinedFields.push(...filteredTopLevel);

    // Add all comp categories second (keep all children for user to select/unselect)
    const filteredComps = separated.comps.filter((field) => {
      // Don't filter children here - keep all children so user can select/unselect them during export
      return field.value || field.children.length > 0;
    });
    combinedFields.push(...filteredComps);

    if (combinedFields.length) {
      exportFieldSettings.set(key, combinedFields);
    }
  });

  return exportFieldSettings;
};

/**
 * Given a collection's name and its exportable items, returns a mapping of eligible items to their fields.
 */
const getExportableFields = async (collectionName: string, items: unknown[]): Promise<ExportFieldSettings> => {
  switch (collectionName) {
    case 'features': {
      return getFeatureExportableFields(items as ExportFeature[]);
    }
    case 'overlays': {
      return getOverlayFields(items as ExportOverlay[]);
    }
    case 'competitiveBuildingComps': {
      return getCompExportableFields(items as ExportComp[]);
    }
    default: {
      // It is currently assumed the default case always handles markers
      const formatFn = exportFormatFunctions.get(collectionName) as unknown as FormatFn;
      return getBuildingExportableFields(items as ExportMarker[], collectionName, formatFn);
    }
  }
};

export {
  getExportableFields,
};
